import { useContext, useSyncExternalStore } from 'react';

import { useFragment } from 'react-relay';

import type { agentSessionsMainQuery_query$key } from './__generated__/agentSessionsMainQuery_query.graphql';
import { AgentSessionsDataContext } from './AgentSessionsDataProvider';
import { agentSessionsMainFragment } from './agentSessionsMainQuery';
import type { MainReference } from './agentSessionsMainResource';
import { useAgentSessionsScope } from './AgentSessionsScopeProvider';
import { normalizeAgentSession } from './normalizeAgentSession';
import type { SharedResourceSnapshot } from './sharedAgentSessionsResource';
import type { AgentSessionsResult } from './types';

const subscribeEmpty = () => () => {};
const emptySnapshot = () => null;

export const useAgentSessionsData = (): AgentSessionsResult => {
	const context = useContext(AgentSessionsDataContext);
	const { reason } = useAgentSessionsScope();
	const resource = context?.resource;
	const snapshot = useSyncExternalStore<SharedResourceSnapshot<MainReference> | null>(
		resource?.subscribe ?? subscribeEmpty,
		resource?.getSnapshot ?? emptySnapshot,
		resource?.getSnapshot ?? emptySnapshot,
	);
	// eslint-disable-next-line @atlassian/relay/no-wrapping-hooks, @atlassian/relay/no-fragment-import -- Public resource readers subscribe to the canonical fragment owned by the single resource provider.
	const data = useFragment<agentSessionsMainQuery_query$key>(
		agentSessionsMainFragment,
		snapshot?.value?.fragment ?? null,
	);
	if (!context) throw new Error('AgentSessionsDataProvider is required.');
	if (reason) return { status: 'unavailable', reason };
	if (!resource || !data || !snapshot?.value)
		throw new Error('AgentSessionsDataProvider has no query reference.');
	const connection = data.agent_sessionQuery;
	return {
		status: 'ready',
		includesLocalAgentSessions: resource.includeLocal,
		sessions:
			connection?.edges?.flatMap((edge) => {
				const session = edge?.node && normalizeAgentSession(edge.node);
				return session ? [session] : [];
			}) ?? [],
		raw: connection,
		pageInfo: snapshot.value.pageInfo,
		isLoadingMore: snapshot.value.isLoadingMore,
		isRefreshing: snapshot.value.isRefreshing,
		loadMoreError: snapshot.value.loadMoreError,
		refreshError: snapshot.value.refreshError,
		loadMore: resource.loadMore,
		refetch: resource.refresh,
	};
};
